import type {
  ListBulkTagsResponse,
  TagCategoryResponse,
  UpdateTagCategoryRequest,
} from '@dashhudson/api-client/community/model';

import {
  getPatchTagCategoriesTagCategoryIdUrl,
  getPatchTagsBulkUrl,
} from '@dashhudson/api-client/community/client';

import type {
  CommunitySettingsCategoriesParams,
  CommunitySettingsCategoriesResponse,
  CommunitySettingsCategory,
  CommunitySettingsTag,
  CommunitySettingsTaggingTask,
  CommunitySettingsTagsParams,
  CommunitySettingsTagsResponse,
} from '@/apis/community-settings.types';
import type { HttpRequestConfig } from '@/apis/dash.http';

import communityApi, { getInteractionRulesForBrands, http } from '@/apis/community';

export function updateCommunitySettingsTagAutoTagging(id: number, autoTaggingEnabled: boolean) {
  return http.patch<ListBulkTagsResponse>(getPatchTagsBulkUrl(), {
    items: [{ id, autoTaggingEnabled }],
  });
}

export function updateCommunitySettingsCategory(
  categoryId: number,
  params: UpdateTagCategoryRequest,
  config: HttpRequestConfig = {},
) {
  return http.patch<TagCategoryResponse>(
    getPatchTagCategoriesTagCategoryIdUrl(categoryId),
    params,
    config,
  );
}

export function getCommunitySettingsTags(
  params: CommunitySettingsTagsParams,
  config: HttpRequestConfig = {},
) {
  return http.get<CommunitySettingsTagsResponse>('/tags', {
    ...config,
    params: {
      ...params,
      brandIds: params.brandIds.join(','),
      categoryIds: params.categoryIds?.join(','),
    },
  });
}

export async function getCommunitySettingsCategories(
  brandIds: number[],
  config: HttpRequestConfig = {},
): Promise<CommunitySettingsCategory[]> {
  const categories: CommunitySettingsCategory[] = [];
  let response = await http.get<CommunitySettingsCategoriesResponse>('/tag_categories', {
    ...config,
    params: { brandIds: brandIds.join(','), limit: 1000, offset: 0 },
  });
  categories.push(...(response.data.data ?? []));
  while (response.data.paging?.next) {
    response = await http.get<CommunitySettingsCategoriesResponse>(response.data.paging.next, {
      ...config,
      params: undefined,
    });
    categories.push(...(response.data.data ?? []));
  }
  return categories;
}

export async function getCommunitySettingsTaggingTasks(
  brandIds: number[],
  config: HttpRequestConfig = {},
): Promise<CommunitySettingsTaggingTask[]> {
  const response = await getInteractionRulesForBrands(brandIds, config);
  const envelope: { data?: readonly CommunitySettingsTaggingTask[] } = response.data;
  return [...(envelope.data ?? [])];
}

export function getCommunitySettingsCategoryPage(
  params: CommunitySettingsCategoriesParams,
  config: HttpRequestConfig = {},
) {
  return http.get<CommunitySettingsCategoriesResponse>('/tag_categories', {
    ...config,
    params: {
      ...params,
      brandIds: params.brandIds.join(','),
      categoryIds: params.categoryIds?.join(','),
      tagIds: params.tagIds?.join(','),
    },
  });
}

export function getCommunitySettingsTagCatalog(
  brandIds: number[],
  config: HttpRequestConfig = {},
): Promise<CommunitySettingsTag[]> {
  return communityApi.getAllTags(brandIds.join(','), config);
}
